// Pure helper that decides which kanban columns to render.
//
// Polish A1 introduced flat-only rules; A2 wired the helper to read
// top-level ``status`` leaves out of an optional advanced-filter
// tree. The follow-up here generalises the tree walk so the column
// set tracks the actual row population the backend will return,
// even when the tree is a complex OR-of-groups (the case the user
// hit: each group constrains ``status``, but at the top level the
// tree is OR, not AND).
//
// Precedence:
//   1. flat ``filters.status`` is set    → [filters.status]
//   2. tree has any status constraint    → union/intersection of
//      the status values that constrain the result set, projected
//      onto the canonical column order
//   3. ``filters.include_cancelled``     → all 6 lanes
//   4. otherwise                         → 5 default open lanes
//
// Tree status derivation (``statusValuesFromTree``):
//   - leaf ``status eq <v>`` / ``status in [v1,v2]`` → those values
//   - any other leaf                                  → null
//     (this branch alone doesn't constrain the column set)
//   - AND[children]                                   → intersection
//     of the children's status sets; null if no child constrains
//   - OR[children]                                    → union of
//     the children's status sets; null if any child is unconstrained
//     (an unconstrained OR-branch can return any status, which
//     means the row set spans all columns)
//
// Concretely: a tree like
//   (due_date>X AND status in [a,b,c]) OR (assignee=u AND status=b) OR status=a
// returns ``[a, b, c]`` because every OR branch constrains ``status``.
// A tree like ``status=a OR priority=high`` returns null (the
// priority branch can return any status), and the column set falls
// back to the default 5.

import {
  COMMITMENT_STATUSES_ORDERED,
  DEFAULT_BOARD_STATUSES,
} from "./commitmentStatusMeta";
import type {
  CommitmentListFilters,
  CommitmentStatus,
} from "./types";
import type { FilterTree } from "./urlState";


export function computeVisibleStatuses(
  filters: CommitmentListFilters,
  filterTree?: FilterTree,
): CommitmentStatus[] {
  // Flat status filter is authoritative.
  if (filters.status) {
    return [filters.status];
  }
  // Tree-driven status constraints (any level).
  const treeStatuses = statusValuesFromTree(filterTree);
  if (treeStatuses && treeStatuses.length > 0) {
    return COMMITMENT_STATUSES_ORDERED.filter((s) =>
      treeStatuses.includes(s),
    );
  }
  if (filters.include_cancelled === true) {
    return [...COMMITMENT_STATUSES_ORDERED];
  }
  return [...DEFAULT_BOARD_STATUSES];
}


/** Returns the set of statuses that the tree's result set is
 * constrained to, or ``null`` when the tree is unconstrained on
 * status (which means rows can be any status, so the caller
 * should fall back to the default column set). */
export function statusValuesFromTree(
  node: FilterTree | undefined,
): CommitmentStatus[] | null {
  if (!node || typeof node !== "object") return null;
  // Leaf.
  if ("field" in node) {
    if (
      node.field === "status"
      && (node.op === "eq" || node.op === "in")
    ) {
      return collectLeafStatusValues(node);
    }
    // Any other leaf alone doesn't constrain status.
    return null;
  }
  // Logical.
  if ("op" in node && Array.isArray((node as { children?: unknown }).children)) {
    const logicalNode = node as {
      op: "and" | "or";
      children: FilterTree[];
    };
    if (logicalNode.op === "and") {
      // AND result rows match every child. If any child constrains
      // status, the result is at most that set; multiple status
      // children intersect.
      let acc: CommitmentStatus[] | null = null;
      for (const child of logicalNode.children) {
        const childStatuses = statusValuesFromTree(child);
        if (childStatuses === null) continue;
        acc = acc === null
          ? [...childStatuses]
          : acc.filter((s) => childStatuses.includes(s));
      }
      return acc;
    }
    if (logicalNode.op === "or") {
      // OR result rows are the union; any unconstrained branch
      // means rows can be anything → fall back.
      const acc: CommitmentStatus[] = [];
      for (const child of logicalNode.children) {
        const childStatuses = statusValuesFromTree(child);
        if (childStatuses === null) return null;
        for (const s of childStatuses) {
          if (!acc.includes(s)) acc.push(s);
        }
      }
      return acc;
    }
  }
  return null;
}


function collectLeafStatusValues(
  leaf: { op: string; value?: unknown },
): CommitmentStatus[] | null {
  if (leaf.op === "eq") {
    if (typeof leaf.value === "string") {
      return [leaf.value as CommitmentStatus];
    }
    return null;
  }
  if (leaf.op === "in" && Array.isArray(leaf.value)) {
    return leaf.value.filter(
      (v): v is CommitmentStatus => typeof v === "string",
    );
  }
  return null;
}
